import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Popover,
  Snackbar,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNotifications } from '../context/NotificationsContext';
import type { ReleaseNoteKind } from '../data/releaseNotes';
import { aboveBottomBar } from './mobile/mobileChrome';

const KIND_COLOR: Record<ReleaseNoteKind, 'success' | 'info' | 'warning'> = {
  feature: 'success',
  improvement: 'info',
  fix: 'warning',
};

/** The bell's list: a waiting new version first, then release notes. */
const NotificationsList: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notes, isUnread, updateAvailable } = useNotifications();

  const kindLabel = (kind: ReleaseNoteKind) => ({
    feature: t('notifications.kindFeature', 'New'),
    improvement: t('notifications.kindImprovement', 'Improved'),
    fix: t('notifications.kindFix', 'Fixed'),
  })[kind];

  return (
    <Box>
      {updateAvailable && (
        <Alert
          severity="info"
          icon={<RefreshIcon fontSize="inherit" />}
          action={(
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {t('notifications.refresh', 'Refresh')}
            </Button>
          )}
          sx={{ borderRadius: 0 }}
        >
          {t('notifications.updateAvailable', 'A new version of ConXEdge is available.')}
        </Alert>
      )}
      {notes.length === 0 && !updateAvailable && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
          {t('notifications.empty', 'No notifications yet.')}
        </Typography>
      )}
      {notes.map((note, i) => {
        const unread = isUnread(note.id);
        return (
          <React.Fragment key={note.id}>
            {i > 0 && <Divider />}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                position: 'relative',
                // Unread notices carry a green left edge, matching the
                // sidebar's selected-item accent.
                borderLeft: '3px solid',
                borderColor: unread ? 'primary.main' : 'transparent',
                bgcolor: unread ? 'rgba(154,208,46,0.06)' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label={kindLabel(note.kind)}
                  color={KIND_COLOR[note.kind]}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(note.date), 'd MMM yyyy')}
                </Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: unread ? 700 : 600, lineHeight: 1.35 }}>
                {note.title}
              </Typography>
              {note.details && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.8rem' }}>
                  {note.details}
                </Typography>
              )}
              {note.link && (
                <Button
                  size="small"
                  onClick={() => { navigate(note.link!); onNavigate(); }}
                  sx={{ mt: 0.5, px: 0, minWidth: 0, fontSize: '0.78rem' }}
                >
                  {t('notifications.open', 'Open')}
                </Button>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const PanelHeader: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
      <Typography id="notifications-title" variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
        {t('notifications.title', 'Notifications')}
      </Typography>
      <IconButton size="small" onClick={onClose} aria-label={t('notifications.close', 'Close notifications')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * Notifications panel: a popover beside the sidebar bell on desktop, a
 * full-screen dialog on phones. Closing it marks everything read.
 */
export const NotificationsPanel: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}> = ({ anchorEl, open, onClose, fullScreen = false }) => {
  const { t } = useTranslation();
  const { markAllRead } = useNotifications();
  const close = () => {
    markAllRead();
    onClose();
  };

  if (fullScreen) {
    return (
      <Dialog open={open} onClose={close} fullScreen aria-labelledby="notifications-title">
        <PanelHeader onClose={close} />
        <Box sx={{ overflowY: 'auto' }}>
          <NotificationsList onNavigate={close} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          'aria-label': t('notifications.title', 'Notifications'),
          sx: { ml: 1, width: 360, maxHeight: '70vh', display: 'flex', flexDirection: 'column' },
        } as object,
      }}
    >
      <PanelHeader onClose={close} />
      <Box sx={{ overflowY: 'auto' }}>
        <NotificationsList onNavigate={close} />
      </Box>
    </Popover>
  );
};

/** Toast shown once a newer build is deployed: Refresh, or close it (the
 *  bell keeps the notice). Sits above the phone bottom bar. */
export const UpdateAvailableBanner: React.FC = () => {
  const { t } = useTranslation();
  const { updateAvailable, bannerDismissed, dismissBanner } = useNotifications();
  return (
    <Snackbar
      open={updateAvailable && !bannerDismissed}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: aboveBottomBar(8), md: 24 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<RefreshIcon fontSize="inherit" />}
        action={(
          <>
            <Button color="inherit" size="small" onClick={() => window.location.reload()} sx={{ fontWeight: 700 }}>
              {t('notifications.refresh', 'Refresh')}
            </Button>
            <IconButton color="inherit" size="small" onClick={dismissBanner} aria-label={t('notifications.dismiss', 'Dismiss')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
        sx={{ alignItems: 'center' }}
      >
        {t('notifications.updateAvailable', 'A new version of ConXEdge is available.')}
      </Alert>
    </Snackbar>
  );
};

export default NotificationsPanel;
